import Swiper from 'swiper';
import { Autoplay, Pagination, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

export default function() {
    const carouselElement = document.querySelector('.communications-carousel');
    
    if (!carouselElement) return;
    
    // Configuration constants
    const AUTOPLAY_DELAY = 10000;
    const RESUME_GUARD_DURATION = 1000;
    
    // Ensure autoplay-stopped class is not present on initialization
    carouselElement.classList.remove('autoplay-stopped');
    
    // State management
    let autoplayDisabled = false;
    let isInitialized = false;
    let isDragging = false;
    let isResumingAutoplay = false;
    
    // DOM elements
    const toggleButton = document.querySelector('.karousel-auto-toggle');
    const rotatingIcon = toggleButton?.querySelector('.karousel-icon-rotating');
    const pausedIcon = toggleButton?.querySelector('.karousel-icon-paused');
    
    // Helper function to stop autoplay
    function stopAutoplay(swiperInstance) {
        autoplayDisabled = true;
        if (swiperInstance.autoplay?.running) {
            swiperInstance.autoplay.stop();
        }
        carouselElement.classList.add('autoplay-stopped');
        toggleButton?.style.setProperty('--progress', '0%');
    }
    
    function updateToggleButton(swiperInstance) {
        if (!toggleButton || !rotatingIcon || !pausedIcon) return;
        
        const shouldShowRunning = !autoplayDisabled && swiperInstance.autoplay;
        
        if (shouldShowRunning) {
            rotatingIcon.style.display = 'block';
            pausedIcon.style.display = 'none';
            toggleButton.setAttribute('aria-label', 'Pause auto-advance');
            toggleButton.setAttribute('title', 'Auto-looping enabled - click to pause');
        } else {
            rotatingIcon.style.display = 'none';
            pausedIcon.style.display = 'block';
            toggleButton.setAttribute('aria-label', 'Resume auto-advance');
            toggleButton.setAttribute('title', 'Auto-looping paused - click to resume');
        }
    }
    
    const swiper = new Swiper('.communications-carousel', {
        modules: [Autoplay, Pagination, Mousewheel],
        
        autoplay: {
            delay: AUTOPLAY_DELAY,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
        },
        
        pagination: {
            el: '.swiper-pagination',
            clickable: true,
            bulletClass: 'karousel-indicator',
            bulletActiveClass: 'active',
        },
        
        loop: true,
        speed: 600,
        
        mousewheel: {
            enabled: true,
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: true,
        },
        
        touchEventsTarget: 'container',
        allowTouchMove: true,
        simulateTouch: true,
        touchRatio: 1,
        touchAngle: 45,
        threshold: 5,
        longSwipesRatio: 0.3,
        shortSwipes: true,
        passiveListeners: true,
        resistanceRatio: 0.85,
        
        slidesPerView: 1,
        spaceBetween: 20,
        
        watchSlidesProgress: true,
        
        on: {
            init() {
                setTimeout(() => {
                    isInitialized = true;
                }, 100);
            },
            
            touchStart() {
                isDragging = false;
            },
            
            touchMove() {
                isDragging = true;
            },
            
            touchEnd() {
                isDragging = false;
            },
            
            sliderMove() {
                if (this.touches?.diff !== 0) {
                    isDragging = true;
                }
            },
            
            autoplayStart() {
                // Reset resuming flag when autoplay starts
                isResumingAutoplay = false;
            },
            
            beforeTransitionStart(swiper, speed, internal) {
                // Don't pause if we just manually resumed autoplay
                if (isResumingAutoplay) return;
                
                // internal === true means autoplay triggered it
                // internal !== true means user triggered it (touchpad, click, drag, etc.)
                if (internal !== true && !autoplayDisabled) {
                    stopAutoplay(this);
                    updateToggleButton(this);
                }
            },
            
            autoplayTimeLeft(swiper, timeLeft, percentage) {
                if (toggleButton) {
                    const progress = (1 - percentage) * 100;
                    toggleButton.style.setProperty('--progress', `${progress}%`);
                }
            },
            
            click(swiper, event) {
                if (isDragging) return;
                
                // Don't stop autoplay if clicking the toggle button or pagination dots
                if (toggleButton && (event.target === toggleButton || toggleButton.contains(event.target))) {
                    return;
                }
                if (event.target.classList.contains('karousel-indicator')) {
                    return;
                }
                
                // Stop autoplay when clicking on slides
                stopAutoplay(this);
            },
            
            slideChange() {
                if (!isInitialized) return;
                
                if (autoplayDisabled || (!this.autoplay.running && this.autoplay.paused)) {
                    autoplayDisabled = true;
                    carouselElement.classList.add('autoplay-stopped');
                }
            },
            
            slideChangeTransitionEnd() {
                if (autoplayDisabled && this.autoplay?.running) {
                    stopAutoplay(this);
                    updateToggleButton(this);
                }
            },
        }
    });
    
    // Play/pause toggle button functionality
    if (toggleButton) {
        toggleButton.addEventListener('click', (event) => {
            event.stopPropagation();
            event.preventDefault();
            
            if (swiper.autoplay?.running) {
                stopAutoplay(swiper);
                isResumingAutoplay = false;
            } else {
                autoplayDisabled = false;
                isResumingAutoplay = true;
                carouselElement.classList.remove('autoplay-stopped');
                swiper.autoplay.start();
                
                setTimeout(() => {
                    isResumingAutoplay = false;
                }, RESUME_GUARD_DURATION);
            }
            
            updateToggleButton(swiper);
        });
        
        swiper.on('autoplayStop', () => updateToggleButton(swiper));
        swiper.on('autoplayStart', () => {
            isResumingAutoplay = false;
            updateToggleButton(swiper);
        });
    }
    
    updateToggleButton(swiper);
}
